import { ButtonV2, DataGridRef } from '@cimplico/components';
import { useRef, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { JournalOverview, JournalStatusEnum } from '../types/journal';
import { isNullOrUndefined } from '../features/new-workpaper/utils';
import { useDeleteJournals } from '../queries/use-delete-journals';
import { toastNotification } from '../components/toast/Toast';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import { WorkpaperRoleEnum } from '../types/workpaper';
import JournalsPagePrimaryActions from '../features/journals/JournalsPagePrimaryActions';
import JournalsTable from '../features/journals/JournalsTable';
import JournalsTabs from '../features/journals/JournalsTabs';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import PostJournalsConfirmDialog from '../features/journals/PostJournalsConfirmDialog';
import VoidJournalsConfirmDialog from '../features/journal-overview/VoidJournalsConfirmDialog';
import DeleteJournalsConfirmDialog from '../features/journal-overview/DeleteJournalsConfirmDialog';
import PostJournalsErrorAlert from '../components/post-journals-error-alert/PostJournalsErrorAlert';
import plural from '../utilities/plural';
import XeroErrorAlert from '../components/XeroErrorAlert';
import NavBreadcrumbs from '../components/nav-breadcrumbs/NavBreadcrumbs';
import GrowHeightContainer from '../components/GrowHeightContainer';
import PageTitle from '../components/PageTitle';

const tabs = ['all', 'draft', 'posted', 'voided'];

function getSavedTab(tab: string | null): number {
    if (!tab) {
        return 0;
    }
    return tabs.includes(tab) ? tabs.indexOf(tab) : 0;
}

function JournalsPage(): JSX.Element {
    const [params, setParams] = useSearchParams();
    const [tab, setTab] = useState(getSavedTab(params.get('tab')));
    const [searchQuery, setSearchQuery] = useState<string | undefined>('');
    const [sortQuery, setSortQuery] = useState<string | undefined>();
    const [filterQuery, setFilterQuery] = useState<string | undefined>(undefined);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [selectedJournals, setSelectedJournals] = useState<JournalOverview[]>([]);
    const [action, setAction] = useState<string | undefined>();
    const [xeroError, setXeroError] = useState<{ name: string; message: string } | undefined>();
    const [errorMessages, setErrorMessages] = useState<{ [key: number]: string[] } | undefined>(undefined);
    const { readOnly, entity, role, permissions } = useWorkpaperContext();

    const tableRef = useRef<DataGridRef | null>(null);
    const navigate = useNavigate();

    const deleteJournalsQuery = useDeleteJournals();

    const roleCanPost =
        role === WorkpaperRoleEnum.REVIEWER ||
        role === WorkpaperRoleEnum.APPROVER ||
        (role === WorkpaperRoleEnum.PREPARER && permissions.preparer.canPostJournal);

    const canPostJournals = selectedJournals.filter(
        (j) =>
            j.status === JournalStatusEnum.DRAFT &&
            !isNullOrUndefined(j.debitTotal) &&
            !isNullOrUndefined(j.creditTotal) &&
            j.debitTotal === j.creditTotal
    );

    const canEditJournals = selectedJournals.filter(
        (j) => j.status !== JournalStatusEnum.VOIDED && j.status !== JournalStatusEnum.ARCHIVED
    );
    const canDeleteJournals = selectedJournals.filter((j) => j.status === JournalStatusEnum.DRAFT);
    const canVoidJournals = selectedJournals.filter((j) => j.status === JournalStatusEnum.POSTED);

    useEffect(() => {
        switch (tab) {
            case 1:
                setStatusFilter('status=DRAFT');
                break;
            case 2:
                setStatusFilter('status=POSTED');
                break;
            case 3:
                setStatusFilter('status=VOIDED');
                break;
            case 4:
                setStatusFilter('status=ARCHIVED');
                break;
            default:
                setStatusFilter(undefined);
        }
    }, [tab]);

    function handleTabChange(t: number): void {
        setTab(t);
        setParams({ tab: tabs[t] });
        resetTable();
    }

    function resetTable(): void {
        setSelectedJournals([]);
        setAction(undefined);
        tableRef?.current?.clear();
    }

    function deleteJournals(): void {
        deleteJournalsQuery.mutate(
            { ids: canDeleteJournals.map((journal) => journal.id) },
            {
                onSuccess: () => {
                    resetTable();
                    toastNotification(
                        'success',
                        `${canDeleteJournals.length} draft ${plural(canDeleteJournals.length, 'journal', 'journals')} deleted!`
                    );
                },
            }
        );
    }

    return (
        <>
            <PostJournalsConfirmDialog
                journalIds={canPostJournals.map((j) => j.id)}
                isOpen={action === 'POST'}
                onClose={(): void => setAction(undefined)}
                onPost={resetTable}
                onXeroError={setXeroError}
                onPostError={setErrorMessages}
            />
            {action === 'DELETE' && (
                <DeleteJournalsConfirmDialog
                    journalIds={canDeleteJournals.map((j) => j.id)}
                    onClose={(): void => setAction(undefined)}
                    onDelete={deleteJournals}
                />
            )}
            {action === 'VOID' && (
                <VoidJournalsConfirmDialog
                    journalIds={canVoidJournals.map((j) => j.id)}
                    onVoid={resetTable}
                    onClose={(): void => setAction(undefined)}
                    onXeroError={setXeroError}
                    onVoidError={setErrorMessages}
                />
            )}
            <div className="pb-4">
                <NavBreadcrumbs />
                {xeroError && (
                    <div className="w-fit mx-auto mb-8">
                        <XeroErrorAlert
                            message={xeroError.message}
                            errorName={xeroError.name}
                            entityName={entity?.name}
                            entityId={entity?.id}
                        />
                    </div>
                )}
                <div className="flex justify-between items-start flex-wrap">
                    <div className="mb-4">
                        <PageTitle title="Journals" />
                        <p className="text-gray-600 text-sm max-w-[600px]">
                            View and manage all journals for this workpaper. To assist with this, you can search and filter the data to view
                            only the worksheets you need to see.
                        </p>
                    </div>
                    {selectedJournals.length === 0 && (
                        <ButtonV2 size="sm" label={'New Journal'} onClick={(): void => navigate('new')} isDisabled={readOnly} />
                    )}
                    {selectedJournals.length > 0 && (
                        <SecondaryPageActions maxActionsCount={4}>
                            {selectedJournals.length === 1 && canEditJournals.length > 0 && (
                                <ButtonV2
                                    type="secondary"
                                    label="Edit Journal"
                                    size="sm"
                                    onClick={(): void => navigate(`./${selectedJournals[0].id}/edit`)}
                                    isDisabled={readOnly}
                                />
                            )}
                            {canPostJournals.length > 0 && (
                                <div>
                                    <ButtonV2
                                        type="secondary"
                                        label={`Post ${plural(canPostJournals.length, 'Journal', 'Journals')} (${canPostJournals.length})`}
                                        size="sm"
                                        onClick={(): void => setAction('POST')}
                                        isDisabled={readOnly || !roleCanPost}
                                        disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                    />
                                </div>
                            )}
                            {canDeleteJournals.length > 0 && (
                                <ButtonV2
                                    type="danger"
                                    label={`Delete ${plural(canDeleteJournals.length, 'Journal', 'Journals')} (${
                                        canDeleteJournals.length
                                    })`}
                                    size="sm"
                                    onClick={(): void => (canDeleteJournals.length > 1 ? setAction('DELETE') : deleteJournals())}
                                    isDisabled={readOnly}
                                />
                            )}
                            {canVoidJournals.length > 0 && (
                                <div>
                                    <ButtonV2
                                        type="danger"
                                        label={`Void ${plural(canVoidJournals.length, 'Journal', 'Journals')} (${canVoidJournals.length})`}
                                        size="sm"
                                        onClick={(): void => setAction('VOID')}
                                        isDisabled={readOnly || !roleCanPost}
                                        disabledHint={!roleCanPost ? 'This requires a reviewer role.' : undefined}
                                    />
                                </div>
                            )}
                        </SecondaryPageActions>
                    )}
                </div>
                <JournalsTabs onTabChange={handleTabChange} defaultIndex={tab} />
                <GrowHeightContainer className="flex flex-wrap items-center gap-2">
                    <JournalsPagePrimaryActions
                        onSearch={setSearchQuery}
                        onSort={setSortQuery}
                        onFilter={setFilterQuery}
                        showStatusConfig={tab === 0}
                        onXeroError={setXeroError}
                        disablePrimaryButton={readOnly}
                    />
                </GrowHeightContainer>
                {errorMessages && <PostJournalsErrorAlert errors={errorMessages} onClose={(): void => setErrorMessages(undefined)} />}
                <JournalsTable
                    tableRef={tableRef}
                    filter={statusFilter ? statusFilter + '&' + filterQuery : filterQuery}
                    search={searchQuery}
                    sort={sortQuery}
                    searchFilters={statusFilter ? 'searchFilters=status' : undefined}
                    onSelect={setSelectedJournals}
                    handleNewJournal={(): void => navigate(`new`)}
                />
            </div>
        </>
    );
}

export default JournalsPage;
